import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import { PRESETS, type PresetDefinition } from "#models/data/presets.ts";
import { Song } from "#models/song.ts";
import { deserializeSong } from "#utils/serialization.ts";
import ChevronDownIcon from "~icons/mdi/chevron-down";

export interface PresetSelectorSignature {
  Args: {
    /** List of presets to display in the selector. Defaults to PRESETS. */
    presets?: PresetDefinition[];
    /** Placeholder label for default empty selection. Defaults to "Presets...". */
    placeholder?: string;
    /** Callback invoked when a preset is selected. */
    onSelect?: (song: Song) => void;
    /** Disabled state for the select dropdown. */
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Dropdown component to select from preset beats.
 */
export default class PresetSelectorComponent extends Component<PresetSelectorSignature> {
  get presets(): PresetDefinition[] {
    return this.args.presets ?? PRESETS;
  }

  get placeholder(): string {
    return this.args.placeholder ?? "Presets...";
  }

  handleChange = (event: Event): void => {
    const select = event.target as HTMLSelectElement;
    const selectedId = select.value;
    select.value = "";
    if (!selectedId) {
      return;
    }
    const preset = this.presets.find((p) => p.id === selectedId);
    if (preset) {
      try {
        const song = deserializeSong(preset.data);
        this.args.onSelect?.(song);
      } catch (err) {
        console.error(`Error loading preset ${preset.name}:`, err);
      }
    }
  };

  <template>
    <div class="relative inline-flex items-center" data-test-preset-selector ...attributes>
      <label for="preset-selector-select" class="sr-only">Select Preset</label>
      <select
        id="preset-selector-select"
        class="appearance-none bg-slate-900 hover:bg-slate-750 text-slate-200 hover:text-white border border-slate-700 hover:border-slate-600 text-xs font-semibold rounded px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        data-test-preset-select
        disabled={{@disabled}}
        {{on "change" this.handleChange}}
      >
        <option value="" disabled selected hidden data-test-preset-placeholder>
          {{this.placeholder}}
        </option>
        {{#each this.presets as |preset|}}
          <option value={{preset.id}} data-test-preset-option={{preset.id}}>
            {{preset.name}}
          </option>
        {{/each}}
      </select>
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400"
        aria-hidden="true"
      >
        <ChevronDownIcon class="w-4 h-4" />
      </div>
    </div>
  </template>
}
